import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly } from "@/lib/date";
import { fmtPts } from "@/components/thespian/ThespianRankCard";

function ClaimSummary({ claim }) {
  return (
    <div className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="thespian-review-summary">
      <p className="font-semibold text-slate-800">{claim.member_name} · {formatDateOnly(claim.activity_date)}</p>
      <p className="text-slate-700">{claim.is_other && <span className="mr-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">Other</span>}{claim.activity_name}</p>
      {claim.is_other
        ? <p className="text-xs text-slate-600" data-testid="thespian-review-other-description">Description: {claim.activity_description}</p>
        : claim.activity_guideline && <p className="text-xs font-medium text-amber-700">Guideline: {claim.activity_guideline}</p>}
      <p className="text-xs text-slate-500">Claimed <span className="font-bold text-slate-700">{fmtPts(claim.points_claimed)}</span> · {claim.notes}</p>
    </div>
  );
}

const done = (setBusy, onClose, onDone, msg) => { setBusy(false); toast.success(msg); onClose(); onDone(); window.dispatchEvent(new Event("rpg:badges-refresh")); };

export function ApproveDialog({ claim, onClose, onDone }) {
  const [points, setPoints] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (claim) { setPoints(String(claim.points_claimed)); setNote(""); } }, [claim]);
  if (!claim) return null;
  const changed = Number(points) !== claim.points_claimed;
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/thespian/claims/${claim.id}/approve`, { points: Number(points), note }); done(setBusy, onClose, onDone, `Approved ${fmtPts(points)} points`); }
    catch (err) { setBusy(false); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="thespian-approve-dialog">
        <DialogHeader><DialogTitle className="font-heading">Approve claim</DialogTitle><DialogDescription>Adjust the points if needed before approving.</DialogDescription></DialogHeader>
        <ClaimSummary claim={claim} />
        <div className="grid grid-cols-[8rem_1fr] gap-3">
          <div className="space-y-2"><Label>Points to award</Label><Input type="number" min="0.5" max="100" step="0.5" value={points} onChange={(e) => setPoints(e.target.value)} data-testid="thespian-approve-points" /></div>
          <div className="space-y-2"><Label>Note to member (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={changed ? "Why the points changed…" : ""} data-testid="thespian-approve-note" /></div>
        </div>
        {changed && points && <p className="text-xs font-medium text-amber-700" data-testid="thespian-approve-changed">Changing from {fmtPts(claim.points_claimed)} to {fmtPts(points)} points.</p>}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={busy || !points} onClick={submit} data-testid="thespian-approve-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Approve {points ? fmtPts(points) : ""} pts</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReturnDialog({ claim, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [claim]);
  if (!claim) return null;
  const submit = async () => {
    if (!note.trim()) { toast.error("Please write a reason for returning"); return; }
    setBusy(true);
    try { await api.post(`/thespian/claims/${claim.id}/return`, { note }); done(setBusy, onClose, onDone, "Claim returned to member"); }
    catch (err) { setBusy(false); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="thespian-return-dialog">
        <DialogHeader><DialogTitle className="font-heading">Return claim</DialogTitle><DialogDescription>The member can fix the claim and resubmit it.</DialogDescription></DialogHeader>
        <ClaimSummary claim={claim} />
        <div className="space-y-2"><Label>Reason (required)</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs to change or what documentation is missing…" data-testid="thespian-return-note" /></div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full bg-violet-600 hover:bg-violet-700" disabled={busy} onClick={submit} data-testid="thespian-return-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Return</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
